/**
 * Hand-rolled iCalendar (RFC 5545) SUBSET for the Calendar tool — import and
 * export of VEVENTs with UID / DTSTAMP / DTSTART / DTEND / SUMMARY /
 * DESCRIPTION / LOCATION / STATUS, including VALUE=DATE all-day events.
 *
 * Deliberately narrow (PLAN §3.7): the risky parts — 75-octet line folding,
 * TEXT escaping, TZID→UTC conversion without a tz library — get dedicated
 * tests against real Google Calendar and Outlook fixture files. The API is
 * library-agnostic on purpose (`parseIcs(text) → {events, skipped}` /
 * `buildIcs(events) → string`) so a later switch to ical.js is a drop-in.
 *
 * RRULE events are NOT expanded: they land in `skipped` with a reason
 * (recurrence import comes later — owner Decision 7).
 */

/* ── unfolding / folding ──────────────────────────────────────────────────── */

/**
 * RFC 5545 §3.1: a CRLF immediately followed by a SPACE or HTAB is a fold and
 * is removed together with that one whitespace octet. Tolerates bare LF and
 * bare CR line endings (real-world files carry all three).
 */
export function unfoldLines(text) {
  const raw = String(text || '').split(/\r\n|\n|\r/);
  const lines = [];
  for (const line of raw) {
    if ((line.startsWith(' ') || line.startsWith('\t')) && lines.length > 0) {
      lines[lines.length - 1] += line.slice(1);
    } else if (line !== '') {
      lines.push(line);
    }
  }
  return lines;
}

/**
 * RFC 5545 §3.1: lines longer than 75 OCTETS (UTF-8 bytes, not chars) must be
 * folded. Splits on byte budget without ever cutting a code point in half
 * (multi-byte chars move whole to the next line).
 */
export function foldLine(line) {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;
  const out = [];
  let current = '';
  let budget = 75;
  for (const ch of line) {
    const chBytes = encoder.encode(ch).length;
    if (encoder.encode(current).length + chBytes > budget) {
      out.push(current);
      current = ' ';
      budget = 75; // continuation lines: 1 leading space + up to 74 octets
    }
    current += ch;
  }
  if (current) out.push(current);
  return out.join('\r\n');
}

/* ── TEXT escaping (RFC 5545 §3.3.11) ─────────────────────────────────────── */

export function escapeText(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

export function unescapeText(value) {
  let out = '';
  const str = String(value ?? '');
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch !== '\\') {
      out += ch;
      continue;
    }
    const next = str[i + 1];
    if (next === 'n' || next === 'N') {
      out += '\n';
      i++;
    } else if (next === '\\' || next === ';' || next === ',') {
      out += next;
      i++;
    } else {
      out += ch; // lone backslash: keep (lenient parse)
    }
  }
  return out;
}

/* ── content-line parsing ─────────────────────────────────────────────────── */

/**
 * Split one unfolded content line into { name, params, value }. Parameter
 * values may be double-quoted and may then contain `:` and `;`
 * (e.g. TZID="America/New_York" from Outlook).
 */
export function parseContentLine(line) {
  let inQuotes = false;
  let colonAt = -1;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === ':' && !inQuotes) {
      colonAt = i;
      break;
    }
  }
  if (colonAt < 0) return null;
  const head = line.slice(0, colonAt);
  const value = line.slice(colonAt + 1);
  const parts = [];
  let current = '';
  inQuotes = false;
  for (const ch of head) {
    if (ch === '"') inQuotes = !inQuotes;
    if (ch === ';' && !inQuotes) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  parts.push(current);
  const name = parts[0].trim().toUpperCase();
  const params = {};
  for (const p of parts.slice(1)) {
    const eq = p.indexOf('=');
    if (eq < 0) continue;
    const key = p.slice(0, eq).trim().toUpperCase();
    let val = p.slice(eq + 1).trim();
    if (val.startsWith('"') && val.endsWith('"')) val = val.slice(1, -1);
    params[key] = val;
  }
  return { name, params, value };
}

/* ── TZID → UTC (the Intl two-pass offset technique, no tz library) ───────── */

function zonedWallTime(epochMs, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = {};
  for (const p of dtf.formatToParts(new Date(epochMs))) parts[p.type] = p.value;
  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // Intl may render midnight as "24"
    Number(parts.minute),
    Number(parts.second)
  );
}

/**
 * Windows time-zone display names → IANA, for the TZIDs real Outlook exports
 * actually carry (Intl only understands IANA). Deliberately the COMMON subset,
 * not the full CLDR windowsZones table — an unmapped TZID lands the event in
 * `skipped` with its name in the reason instead of silently mis-timing it.
 */
const WINDOWS_TZ_MAP = {
  'Dateline Standard Time': 'Etc/GMT+12',
  'UTC-11': 'Etc/GMT+11',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'Alaskan Standard Time': 'America/Anchorage',
  'Pacific Standard Time': 'America/Los_Angeles',
  'US Mountain Standard Time': 'America/Phoenix',
  'Mountain Standard Time': 'America/Denver',
  'Central Standard Time': 'America/Chicago',
  'Central America Standard Time': 'America/Guatemala',
  'Eastern Standard Time': 'America/New_York',
  'US Eastern Standard Time': 'America/Indiana/Indianapolis',
  'Atlantic Standard Time': 'America/Halifax',
  'SA Eastern Standard Time': 'America/Cayenne',
  'Argentina Standard Time': 'America/Argentina/Buenos_Aires',
  'E. South America Standard Time': 'America/Sao_Paulo',
  UTC: 'Etc/UTC',
  'GMT Standard Time': 'Europe/London',
  'Greenwich Standard Time': 'Atlantic/Reykjavik',
  'W. Europe Standard Time': 'Europe/Berlin',
  'Central Europe Standard Time': 'Europe/Budapest',
  'Romance Standard Time': 'Europe/Paris',
  'Central European Standard Time': 'Europe/Warsaw',
  'E. Europe Standard Time': 'Europe/Chisinau',
  'FLE Standard Time': 'Europe/Kiev',
  'GTB Standard Time': 'Europe/Bucharest',
  'Turkey Standard Time': 'Europe/Istanbul',
  'Russian Standard Time': 'Europe/Moscow',
  'Israel Standard Time': 'Asia/Jerusalem',
  'Arabian Standard Time': 'Asia/Dubai',
  'India Standard Time': 'Asia/Kolkata',
  'SE Asia Standard Time': 'Asia/Bangkok',
  'China Standard Time': 'Asia/Shanghai',
  'Singapore Standard Time': 'Asia/Singapore',
  'Tokyo Standard Time': 'Asia/Tokyo',
  'Korea Standard Time': 'Asia/Seoul',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'W. Australia Standard Time': 'Australia/Perth',
  'New Zealand Standard Time': 'Pacific/Auckland',
};

/** Resolve an ics TZID to something Intl accepts (IANA passes through). */
export function resolveTzid(tzid) {
  return WINDOWS_TZ_MAP[tzid] || tzid;
}

/**
 * Epoch ms of the wall-clock time `{y,mo,d,h,mi,s}` IN `timeZone`.
 *
 * Two passes: guess the epoch as if the wall time were UTC, read back what
 * wall time that epoch actually shows in the zone, and correct by the
 * difference; a second correction round absorbs a DST boundary between guess
 * and answer. Nonexistent wall times (spring-forward gap) resolve to the
 * instant after the jump — the same lenient choice Google Calendar makes.
 * Throws on an unknown TZID so the caller can put the event in `skipped`
 * rather than silently mis-timing it.
 */
export function zonedTimeToUtc({ y, mo, d, h, mi, s }, timeZone) {
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  let guess = asUtc;
  for (let pass = 0; pass < 2; pass++) {
    const wall = zonedWallTime(guess, timeZone); // throws on bad TZID
    const diff = asUtc - wall;
    if (diff === 0) return guess;
    guess += diff;
  }
  return guess;
}

/* ── date value parsing ───────────────────────────────────────────────────── */

const DATE_RE = /^(\d{4})(\d{2})(\d{2})$/;
const DATETIME_RE = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/;

/**
 * One DTSTART/DTEND value → { allDay, dateKey } | { allDay:false, iso } and
 * the tz the instant was declared in ('UTC', a TZID, or 'floating').
 */
function parseDateValue(value, params) {
  const isDate = (params.VALUE || '').toUpperCase() === 'DATE' || DATE_RE.test(value);
  if (isDate) {
    const m = DATE_RE.exec(value);
    if (!m) throw new Error(`unparseable DATE value "${value}"`);
    return { allDay: true, dateKey: `${m[1]}-${m[2]}-${m[3]}` };
  }
  const m = DATETIME_RE.exec(value);
  if (!m) throw new Error(`unparseable DATE-TIME value "${value}"`);
  const parts = {
    y: Number(m[1]),
    mo: Number(m[2]),
    d: Number(m[3]),
    h: Number(m[4]),
    mi: Number(m[5]),
    s: Number(m[6]),
  };
  if (m[7] === 'Z') {
    return {
      allDay: false,
      iso: new Date(Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, parts.s)).toISOString(),
      tz: 'UTC',
    };
  }
  if (params.TZID) {
    const zone = resolveTzid(params.TZID);
    let epoch;
    try {
      epoch = zonedTimeToUtc(parts, zone);
    } catch {
      throw new Error(`unknown time zone "${params.TZID}"`);
    }
    return { allDay: false, iso: new Date(epoch).toISOString(), tz: zone };
  }
  // Floating time: interpret in the device's local zone (lenient; rare).
  const local = new Date(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, parts.s);
  return { allDay: false, iso: local.toISOString(), tz: 'floating' };
}

/** Bare YYYY-MM-DD key → previous day's key (ics all-day DTEND is exclusive). */
function dateKeyMinusOneDay(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() - 1);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/* ── recurrence (Phase 4, PLAN.recurrence §5) ─────────────────────────────── */

const SUPPORTED_RRULE_PARTS = new Set(['FREQ', 'INTERVAL', 'COUNT', 'UNTIL', 'BYDAY', 'WKST']);
const PLAIN_BYDAY = new Set(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']);

/** Local YYYY-MM-DD key of a local Date (import-side occurrence identity). */
function localDateKey(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * RRULE → the §3.2 subset rule, or a thrown Error naming the offending part —
 * a rule we can't honor skips the WHOLE event, never a partial import.
 */
export function parseRRule(value) {
  const parts = {};
  for (const chunk of String(value || '').split(';')) {
    if (!chunk) continue;
    const eq = chunk.indexOf('=');
    if (eq < 0) throw new Error(`unsupported RRULE part "${chunk}"`);
    parts[chunk.slice(0, eq).trim().toUpperCase()] = chunk.slice(eq + 1).trim();
  }
  for (const key of Object.keys(parts)) {
    if (!SUPPORTED_RRULE_PARTS.has(key)) throw new Error(`unsupported RRULE part "${key}"`);
  }
  const freqRaw = (parts.FREQ || '').toUpperCase();
  if (!['DAILY', 'WEEKLY', 'MONTHLY'].includes(freqRaw)) {
    throw new Error(`unsupported RRULE part "FREQ=${parts.FREQ}"`);
  }
  if (parts.WKST && parts.WKST.toUpperCase() !== 'MO') {
    throw new Error(`unsupported RRULE part "WKST=${parts.WKST}"`);
  }
  if (parts.COUNT != null && parts.UNTIL != null) {
    throw new Error('unsupported RRULE part "COUNT+UNTIL"');
  }
  const rule = { freq: freqRaw.toLowerCase(), interval: 1 };
  if (parts.INTERVAL != null) {
    rule.interval = Number(parts.INTERVAL);
    if (!Number.isInteger(rule.interval) || rule.interval < 1) {
      throw new Error(`unsupported RRULE part "INTERVAL=${parts.INTERVAL}"`);
    }
  }
  if (parts.COUNT != null) {
    rule.count = Number(parts.COUNT);
    if (!Number.isInteger(rule.count) || rule.count < 1) {
      throw new Error(`unsupported RRULE part "COUNT=${parts.COUNT}"`);
    }
  }
  if (parts.UNTIL != null) {
    // UTC date-time or bare DATE (RFC) → LOCAL inclusive date key.
    const dm = DATE_RE.exec(parts.UNTIL);
    const dtm = DATETIME_RE.exec(parts.UNTIL);
    if (dm) rule.until = `${dm[1]}-${dm[2]}-${dm[3]}`;
    else if (dtm && dtm[7] === 'Z') {
      rule.until = localDateKey(
        new Date(Date.UTC(+dtm[1], +dtm[2] - 1, +dtm[3], +dtm[4], +dtm[5], +dtm[6]))
      );
    } else throw new Error(`unsupported RRULE part "UNTIL=${parts.UNTIL}"`);
  }
  if (parts.BYDAY != null) {
    if (freqRaw !== 'WEEKLY') throw new Error('unsupported RRULE part "BYDAY outside WEEKLY"');
    const codes = parts.BYDAY.split(',').map((c) => c.trim().toUpperCase());
    for (const code of codes) {
      // Ordinal BYDAY (e.g. 2MO, -1FR) is outside the subset.
      if (!PLAIN_BYDAY.has(code)) throw new Error(`unsupported RRULE part "BYDAY=${code}"`);
    }
    rule.by_day = codes;
  }
  return rule;
}

/** One EXDATE / RECURRENCE-ID value → local occurrence date key. */
function dateValueToLocalKey(value, params) {
  const parsed = parseDateValue(value, params); // throws on garbage/unknown TZID
  if (parsed.allDay) return parsed.dateKey;
  return localDateKey(new Date(parsed.iso));
}

/* ── parse ────────────────────────────────────────────────────────────────── */

function newUid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `ics-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Parse an .ics text into Calendar client event records (PLAN §3.2 shape).
 *
 * @returns {{ events: Array<object>, skipped: Array<{summary: string, reason: string}> }}
 *   `skipped` lists every VEVENT that was NOT imported and why (RRULE,
 *   missing DTSTART, unknown TZID, unparseable dates) — the import summary
 *   modal renders it verbatim. Non-VEVENT components (VTIMEZONE, VTODO) and
 *   VALARM sub-components are ignored without a report: they are not events.
 */
export function parseIcs(text) {
  const lines = unfoldLines(text);
  const events = [];
  const skipped = [];

  let inEvent = false;
  let nestedDepth = 0; // inside VALARM (or other sub-component) of the event
  let outerDepth = 0; // inside a top-level non-VEVENT component (VTIMEZONE…)
  let props = null;

  const finishEvent = () => {
    const summary = props.SUMMARY ? unescapeText(props.SUMMARY.value) : '(no title)';
    try {
      if (!props.DTSTART) {
        skipped.push({ summary, reason: 'missing DTSTART' });
        return;
      }
      const start = parseDateValue(props.DTSTART.value, props.DTSTART.params);
      let end = null;
      if (props.DTEND) {
        end = parseDateValue(props.DTEND.value, props.DTEND.params);
      }
      const nowIso = new Date().toISOString();
      const statusRaw = props.STATUS ? props.STATUS.value.trim().toUpperCase() : '';
      const event = {
        uid: props.UID ? props.UID.value.trim() : newUid(),
        title: summary === '(no title)' && !props.SUMMARY ? '' : summary,
        description: props.DESCRIPTION ? unescapeText(props.DESCRIPTION.value) : '',
        location: props.LOCATION ? unescapeText(props.LOCATION.value) : '',
        all_day: start.allDay,
        calendar: 'personal',
        color: null,
        reminders: [],
        status: statusRaw === 'CANCELLED' ? 'cancelled' : 'confirmed',
        created_at: nowIso,
        updated_at: nowIso,
      };
      if (start.allDay) {
        event.start_date = start.dateKey;
        // ics DTEND;VALUE=DATE is EXCLUSIVE; our record's end_date is
        // inclusive (RFC 5545 §3.8.2.2 default duration: one day when absent).
        event.end_date =
          end && end.allDay ? dateKeyMinusOneDay(end.dateKey) : start.dateKey;
        if (event.end_date < event.start_date) event.end_date = event.start_date;
        event.start = null;
        event.end = null;
        event.tz = null;
      } else {
        event.start = start.iso;
        // RFC 5545 §3.6.1: absent DTEND on a DATE-TIME start = zero duration.
        event.end = end && !end.allDay ? end.iso : start.iso;
        if (new Date(event.end) < new Date(event.start)) event.end = event.start;
        event.tz =
          start.tz && start.tz !== 'floating' && start.tz !== 'UTC'
            ? start.tz
            : (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
        event.start_date = null;
        event.end_date = null;
      }
      // Phase 4: the RRULE subset imports; anything outside it throws with
      // the offending part named → the WHOLE event lands in `skipped`.
      if (props.RRULE) {
        event.recurrence = parseRRule(props.RRULE.value);
      }
      if (props.EXDATE_ALL) {
        const keys = [];
        for (const prop of props.EXDATE_ALL) {
          for (const v of prop.value.split(',')) {
            if (v.trim()) keys.push(dateValueToLocalKey(v.trim(), prop.params));
          }
        }
        event.exdates = [...new Set(keys)];
      }
      if (props['RECURRENCE-ID']) {
        // Detached override (real Google exports) — linked to its master in
        // the post-pass below; kept raw here so TZID conversion happens once.
        event.__recurrence_id = {
          value: props['RECURRENCE-ID'].value,
          params: props['RECURRENCE-ID'].params,
        };
      }
      events.push(event);
    } catch (err) {
      skipped.push({ summary, reason: String(err?.message || err) });
    }
  };

  for (const line of lines) {
    const upper = line.toUpperCase();
    if (!inEvent) {
      // Only a TOP-LEVEL VEVENT is an event. A VEVENT smuggled inside a
      // VTIMEZONE (or any other component) must not import (fix round 1, N4)
      // — real VTIMEZONEs carry STANDARD/DAYLIGHT sub-components, which this
      // depth tracking also covers.
      if (upper === 'BEGIN:VEVENT' && outerDepth === 0) {
        inEvent = true;
        nestedDepth = 0;
        props = {};
      } else if (upper.startsWith('BEGIN:') && upper !== 'BEGIN:VCALENDAR') {
        outerDepth += 1;
      } else if (upper.startsWith('END:') && upper !== 'END:VCALENDAR' && outerDepth > 0) {
        outerDepth -= 1;
      }
      continue;
    }
    // Sub-components (VALARM) carry their own DESCRIPTION/SUMMARY — those
    // must never overwrite the event's, so everything nested is skipped.
    if (upper.startsWith('BEGIN:')) {
      nestedDepth += 1;
      continue;
    }
    if (upper.startsWith('END:')) {
      if (nestedDepth > 0) {
        nestedDepth -= 1;
        continue;
      }
      if (upper === 'END:VEVENT') {
        finishEvent();
        inEvent = false;
        props = null;
      }
      continue;
    }
    if (nestedDepth > 0) continue;
    const parsed = parseContentLine(line);
    if (!parsed) continue;
    // EXDATE legitimately repeats and carries comma lists — ACCUMULATE it
    // (Phase 4); every other property keeps first-wins (duplicates invalid).
    if (parsed.name === 'EXDATE') {
      (props.EXDATE_ALL = props.EXDATE_ALL || []).push(parsed);
      continue;
    }
    if (!props[parsed.name]) props[parsed.name] = parsed;
  }

  // Post-pass: RECURRENCE-ID events become override records IF their master
  // (same UID, with a rule) imported from the same file; otherwise skipped
  // with a reason (PLAN.recurrence §5).
  const masters = new Map(
    events.filter((e) => e.recurrence && !e.__recurrence_id).map((e) => [e.uid, e])
  );
  const linked = [];
  for (const e of events) {
    if (!e.__recurrence_id) {
      linked.push(e);
      continue;
    }
    const master = masters.get(e.uid);
    const label = e.title || '(no title)';
    if (!master) {
      skipped.push({ summary: label, reason: 'RECURRENCE-ID without its recurring master in this file' });
      continue;
    }
    let occurrenceDate;
    try {
      occurrenceDate = dateValueToLocalKey(e.__recurrence_id.value, e.__recurrence_id.params);
    } catch (err) {
      skipped.push({ summary: label, reason: String(err?.message || err) });
      continue;
    }
    const { __recurrence_id, ...rest } = e;
    linked.push({
      ...rest,
      // Deterministic distinct uid (master + override share UID on the wire).
      uid: `${master.uid}::${occurrenceDate}`,
      recurrence: null,
      override_of: { master_uid: master.uid, occurrence_date: occurrenceDate },
    });
  }
  return { events: linked, skipped };
}

/* ── build ────────────────────────────────────────────────────────────────── */

function toBasicUtc(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
  );
}

function toBasicDate(dateKey) {
  return String(dateKey || '').replace(/-/g, '');
}

function dateKeyPlusOneDay(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() + 1);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/** Local Date at a YYYY-MM-DD key + the wall-clock minutes of `localStart`. */
function localOccurrenceStart(dateKey, localStart) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ''));
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setHours(localStart.getHours(), localStart.getMinutes(), 0, 0);
  return d;
}

/** RRULE line for a §3.2 rule (UNTIL in the DTSTART's own form per RFC). */
function buildRRuleLine(event) {
  const rule = event.recurrence;
  const parts = [`FREQ=${rule.freq.toUpperCase()}`];
  if ((rule.interval ?? 1) > 1) parts.push(`INTERVAL=${rule.interval}`);
  if (rule.by_day?.length) parts.push(`BYDAY=${rule.by_day.join(',')}`);
  if (rule.count != null) parts.push(`COUNT=${rule.count}`);
  if (rule.until != null) {
    if (event.all_day) parts.push(`UNTIL=${toBasicDate(rule.until)}`);
    else {
      // UTC date-time of that day's occurrence start (plan decision).
      const start = localOccurrenceStart(rule.until, new Date(event.start));
      parts.push(`UNTIL=${toBasicUtc(start.toISOString())}`);
    }
  }
  return `RRULE:${parts.join(';')}`;
}

/**
 * Serialize Calendar client event records to a VCALENDAR string (CRLF line
 * endings, 75-octet folding, escaped TEXT values; UTC `Z` form for timed
 * events, VALUE=DATE with exclusive DTEND for all-day ones).
 */
export function buildIcs(events, { now = new Date() } = {}) {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Apiboo//Calendar//EN',
    'CALSCALE:GREGORIAN',
  ];
  const stamp = toBasicUtc(now.toISOString());
  const mastersByUid = new Map(
    (events || []).filter((e) => e?.recurrence).map((e) => [e.uid, e])
  );
  for (const event of events || []) {
    // Overrides export as sibling VEVENTs with the MASTER's UID +
    // RECURRENCE-ID (RFC shape); a master missing from this export degrades
    // the override to a standalone single event (own UID, no RECURRENCE-ID).
    const master = event.override_of ? mastersByUid.get(event.override_of.master_uid) : null;
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${escapeText(master ? master.uid : event.uid)}`);
    lines.push(`DTSTAMP:${stamp}`);
    if (master) {
      if (master.all_day) {
        lines.push(`RECURRENCE-ID;VALUE=DATE:${toBasicDate(event.override_of.occurrence_date)}`);
      } else {
        const occStart = localOccurrenceStart(
          event.override_of.occurrence_date,
          new Date(master.start)
        );
        lines.push(`RECURRENCE-ID:${toBasicUtc(occStart.toISOString())}`);
      }
    }
    if (event.all_day) {
      lines.push(`DTSTART;VALUE=DATE:${toBasicDate(event.start_date)}`);
      lines.push(
        `DTEND;VALUE=DATE:${toBasicDate(dateKeyPlusOneDay(event.end_date || event.start_date))}`
      );
    } else {
      lines.push(`DTSTART:${toBasicUtc(event.start)}`);
      lines.push(`DTEND:${toBasicUtc(event.end)}`);
    }
    lines.push(`SUMMARY:${escapeText(event.title)}`);
    if (event.description) lines.push(`DESCRIPTION:${escapeText(event.description)}`);
    if (event.location) lines.push(`LOCATION:${escapeText(event.location)}`);
    lines.push(`STATUS:${event.status === 'cancelled' ? 'CANCELLED' : 'CONFIRMED'}`);
    if (event.recurrence) {
      lines.push(buildRRuleLine(event));
      for (const key of event.exdates || []) {
        if (event.all_day) lines.push(`EXDATE;VALUE=DATE:${toBasicDate(key)}`);
        else {
          const occStart = localOccurrenceStart(key, new Date(event.start));
          lines.push(`EXDATE:${toBasicUtc(occStart.toISOString())}`);
        }
      }
    }
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}
